import { memo, useEffect, useId, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import type { RootState } from "../../Store/store";
import {
  contactErrorMessage,
  type InquiryTopic,
  useSubmitContactFormMutation,
} from "../../Services/api.contact.slice";

/**
 * "Send us a message", anywhere on the site (October 2026). Asher: every dead
 * end should let people send a message, and those messages must reach him.
 * The backend saves each one before emailing info@simplerhome.app, so a sent
 * message is never lost; this form only has to make sending easy and make the
 * result unmistakable.
 *
 * Signed-in visitors get their email filled in. The page they're on (and the
 * home, if there is one) goes along with the message so Asher has context.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MESSAGE_MAX = 5000;

export type MessageFormProps = {
  topic?: InquiryTopic;
  /** Pre-filled subject; shown as an editable field only when showSubject. */
  defaultSubject?: string;
  defaultMessage?: string;
  messageLabel?: string;
  messagePlaceholder?: string;
  showSubject?: boolean;
  showPhone?: boolean;
  submitLabel?: string;
  /** Context that travels with the message. */
  propertyId?: string;
  propertyLabel?: string;
  trade?: string;
  /** White fields on a tinted panel (true) or plain (false). */
  onTint?: boolean;
  onSent?: () => void;
  /** Shown under the success message, e.g. a Close button in a dialog. */
  successAction?: React.ReactNode;
};

type Fields = { name: string; email: string; phone: string; subject: string; message: string; website: string };
type FieldErrors = Partial<Record<"name" | "email" | "message", string>>;

const MessageForm = memo(
  ({
    topic = "general",
    defaultSubject = "",
    defaultMessage = "",
    messageLabel = "Message",
    messagePlaceholder = "How can we help?",
    showSubject = false,
    showPhone = false,
    submitLabel = "Send message",
    propertyId,
    propertyLabel,
    trade,
    onTint = false,
    onSent,
    successAction,
  }: MessageFormProps) => {
    const uid = useId();
    const location = useLocation();
    const signedInEmail = useSelector((state: RootState) =>
      state?.auth?.isAuthenticated ? state?.auth?.user?.email || "" : ""
    );

    const [fields, setFields] = useState<Fields>({
      name: "",
      email: signedInEmail,
      phone: "",
      subject: defaultSubject,
      message: defaultMessage,
      website: "",
    });
    const [errors, setErrors] = useState<FieldErrors>({});
    const [serverError, setServerError] = useState("");
    const [sentTo, setSentTo] = useState<string | null>(null);
    const [submit, { isLoading }] = useSubmitContactFormMutation();

    // Signing in after the form mounted (e.g. from the header) fills the email,
    // unless they've already typed one.
    useEffect(() => {
      if (signedInEmail) setFields((f) => (f.email ? f : { ...f, email: signedInEmail }));
    }, [signedInEmail]);

    const set = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((f) => ({ ...f, [key]: value }));
      if (key in errors) setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const validate = (): FieldErrors => {
      const er: FieldErrors = {};
      if (!fields.name.trim()) er.name = "Please enter your name.";
      if (!EMAIL_RE.test(fields.email.trim())) er.email = "Please enter a valid email so we can reply.";
      if (!fields.message.trim()) er.message = "Please write a message.";
      return er;
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setServerError("");
      const er = validate();
      setErrors(er);
      if (Object.values(er).some(Boolean)) return;
      try {
        await submit({
          topic,
          name: fields.name.trim(),
          email: fields.email.trim(),
          phone: fields.phone.trim() || undefined,
          subject: fields.subject.trim() || undefined,
          message: fields.message.trim(),
          pageUrl: `${location.pathname}${location.search}`,
          propertyId,
          propertyLabel,
          trade,
          website: fields.website || undefined,
        }).unwrap();
        setSentTo(fields.email.trim());
        onSent?.();
      } catch (err) {
        setServerError(contactErrorMessage(err));
      }
    };

    const sendAnother = () => {
      setSentTo(null);
      setFields((f) => ({ ...f, subject: defaultSubject, message: "", phone: "" }));
    };

    const fieldSx = onTint ? { "& .MuiOutlinedInput-root": { backgroundColor: "#ffffff" } } : undefined;

    if (sentTo) {
      return (
        <Stack
          role="status"
          data-testid="message-form-sent"
          alignItems="center"
          textAlign="center"
          spacing={1.5}
          sx={{ py: 3 }}
        >
          <CheckCircleOutlineRounded sx={{ fontSize: 48, color: "success.main" }} />
          <Typography variant="h6" fontWeight={700}>
            Message sent
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            It reached the SimplerHome team. We'll reply to <strong>{sentTo}</strong>, usually within one business
            day.
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
            {successAction}
            <Button variant="text" onClick={sendAnother} sx={{ textTransform: "none" }}>
              Send another message
            </Button>
          </Stack>
        </Stack>
      );
    }

    return (
      <Box component="form" noValidate onSubmit={handleSubmit} data-testid="message-form" aria-busy={isLoading}>
        <Stack spacing={2}>
          {serverError && (
            <Alert severity="error" onClose={() => setServerError("")}>
              {serverError}
            </Alert>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              id={`${uid}-name`}
              label="Your name"
              value={fields.name}
              onChange={set("name")}
              error={Boolean(errors.name)}
              helperText={errors.name}
              autoComplete="name"
              required
              fullWidth
              sx={fieldSx}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              id={`${uid}-email`}
              label="Your email"
              type="email"
              value={fields.email}
              onChange={set("email")}
              error={Boolean(errors.email)}
              helperText={errors.email}
              autoComplete="email"
              required
              fullWidth
              sx={fieldSx}
              inputProps={{ maxLength: 200 }}
            />
          </Box>

          {showPhone && (
            <TextField
              id={`${uid}-phone`}
              label="Phone (optional)"
              type="tel"
              value={fields.phone}
              onChange={set("phone")}
              autoComplete="tel"
              helperText="If you'd rather we call."
              fullWidth
              sx={fieldSx}
              inputProps={{ maxLength: 40 }}
            />
          )}

          {showSubject && (
            <TextField
              id={`${uid}-subject`}
              label="Subject (optional)"
              value={fields.subject}
              onChange={set("subject")}
              fullWidth
              sx={fieldSx}
              inputProps={{ maxLength: 200 }}
            />
          )}

          <TextField
            id={`${uid}-message`}
            label={messageLabel}
            placeholder={messagePlaceholder}
            value={fields.message}
            onChange={set("message")}
            error={Boolean(errors.message)}
            helperText={
              errors.message ||
              (fields.message.length > MESSAGE_MAX - 500 ? `${MESSAGE_MAX - fields.message.length} characters left` : " ")
            }
            required
            fullWidth
            multiline
            minRows={4}
            sx={fieldSx}
            inputProps={{ maxLength: MESSAGE_MAX }}
          />

          {/* Honeypot. Off-screen rather than display:none, which some bots skip. */}
          <Box aria-hidden="true" sx={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
            <label htmlFor={`${uid}-website`}>Leave this empty</label>
            <input
              id={`${uid}-website`}
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={fields.website}
              onChange={set("website")}
            />
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 2.5 }} alignItems={{ sm: "center" }}>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              size="large"
              disabled={isLoading}
              sx={{ textTransform: "none", color: "#fff", borderRadius: "8px", px: 4, minWidth: 180 }}
            >
              {isLoading ? <CircularProgress size={22} color="inherit" aria-label="Sending" /> : submitLabel}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Goes straight to the SimplerHome team.
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  }
);

export default MessageForm;
